import { Controller } from "@hotwired/stimulus";

/**
 * FK Config Controller
 *
 * Manages the Foreign Key configuration UI for editing FK/PK fields.
 * Provides a guided interface for configuring lookup settings.
 */
export default class extends Controller {
  static targets = [
    "collapseToggle",
    "header",
    "content",
    "hiddenField",
    "lookupKey",
    "lookupModel",
    "lookupScope",
    "displayMethod",
    "searchExpression",
    "valueAttribute",
    "linkable",
    "linkPathContainer",
    "linkPathHelper",
    "parentAttribute",
    "optgroupAttribute",
  ];

  static values = {
    fieldType: String,
    initial: String,
    availableLookups: Array,
  };

  connect() {
    // Only show for foreign_key and primary_key field types
    this.updateVisibility();

    // Parse initial config and populate fields
    if (this.initialValue) {
      try {
        const config = JSON.parse(this.initialValue);
        this.populateFields(config);
      } catch (e) {
        console.warn("Failed to parse initial FK config:", e);
      }
    }
  }

  updateVisibility() {
    const isFkType = this.fieldTypeValue === "foreign_key" || this.fieldTypeValue === "primary_key";
    this.element.classList.toggle("hidden", !isFkType);

    // Enable/disable hidden fields to ensure only one filter_config is submitted
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.disabled = !isFkType;
    }

    // Disable the enum-values hidden field when FK config is active
    const enumHiddenField = document.querySelector('[data-enum-values-target="hidden"]');
    if (enumHiddenField) {
      enumHiddenField.disabled = isFkType;
    }
  }

  handleFieldTypeChange(event) {
    const newType = event.target.value;
    this.fieldTypeValue = newType;
    this.updateVisibility();
  }

  populateFields(config) {
    // If config has a lookup_key, select it in the dropdown
    if (this.hasLookupKeyTarget && config.lookup_key) {
      this.lookupKeyTarget.value = config.lookup_key;
      // Resolve the lookup definition from availableLookups array and merge with overrides
      const lookup = this.availableLookupsValue?.find(l => l.key === config.lookup_key);
      const lookupDef = lookup?.definition || {};
      const mergedConfig = { ...lookupDef, lookup_model: lookup?.model, ...config };
      this.populateFieldsFromConfig(mergedConfig);
    } else {
      if (this.hasLookupKeyTarget) {
        this.lookupKeyTarget.value = "";
      }
      this.populateFieldsFromConfig(config);
    }
  }

  populateFieldsFromConfig(config) {
    if (this.hasLookupModelTarget) {
      this.lookupModelTarget.value = config.lookup_model || "";
    }
    if (this.hasLookupScopeTarget) {
      this.lookupScopeTarget.value = config.lookup_scope || "";
    }
    if (this.hasDisplayMethodTarget) {
      // Support both new display_method and legacy display_attribute/lookup_display_method
      this.displayMethodTarget.value = config.display_method || config.display_attribute || config.lookup_display_method || "";
    }
    if (this.hasSearchExpressionTarget) {
      this.searchExpressionTarget.value = config.search_expression || "";
    }
    if (this.hasValueAttributeTarget) {
      this.valueAttributeTarget.value = config.value_attribute || "id";
    }
    if (this.hasLinkableTarget) {
      this.linkableTarget.checked = config.linkable || false;
    }
    if (this.hasLinkPathHelperTarget) {
      this.linkPathHelperTarget.value = config.link_path_helper || "";
    }
    if (this.hasParentAttributeTarget) {
      this.parentAttributeTarget.value = config.parent_attribute || "";
    }
    if (this.hasOptgroupAttributeTarget) {
      this.optgroupAttributeTarget.value = config.optgroup_attribute || "";
    }

    // Show/hide link path based on linkable state
    this.toggleLinkable();
  }

  // Handle selection of a predefined lookup definition
  selectLookupDefinition(event) {
    const lookupKey = event.target.value;

    if (lookupKey) {
      // Get the definition and model from the data attributes
      const option = event.target.selectedOptions[0];
      const definitionJson = option?.dataset?.definition;
      const model = option?.dataset?.model;

      if (definitionJson) {
        try {
          const definition = JSON.parse(definitionJson);
          // Merge in the model from the lookup
          this.populateFieldsFromConfig({ ...definition, lookup_model: model });
        } catch (e) {
          console.warn("Failed to parse lookup definition:", e);
        }
      }
    }

    // Update the hidden config to include lookup_key
    this.updateConfig();
  }

  toggleLinkable() {
    if (!this.hasLinkableTarget || !this.hasLinkPathContainerTarget) return;

    const isLinkable = this.linkableTarget.checked;
    this.linkPathContainerTarget.classList.toggle("hidden", !isLinkable);
    this.updateConfig();
  }

  updateConfig() {
    const config = this.buildConfig();

    // Update the dedicated FK config hidden field
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = JSON.stringify(config);
    }
  }

  buildConfig() {
    const config = {};

    // Check if a lookup_key is selected
    const lookupKey = this.hasLookupKeyTarget ? this.lookupKeyTarget.value.trim() : "";

    if (lookupKey) {
      // When using a lookup_key, only include the key and any overrides
      config.lookup_key = lookupKey;

      // Get the base definition from availableLookups array to compare for overrides
      const lookup = this.availableLookupsValue?.find(l => l.key === lookupKey);
      const baseDef = lookup?.definition || {};

      // Only include values that differ from the base definition (overrides)
      const lookupModel = this.hasLookupModelTarget ? this.lookupModelTarget.value.trim() : "";
      if (lookupModel && lookupModel !== baseDef.lookup_model) {
        config.lookup_model = lookupModel;
      }

      const lookupScope = this.hasLookupScopeTarget ? this.lookupScopeTarget.value.trim() : "";
      if (lookupScope && lookupScope !== baseDef.lookup_scope) {
        config.lookup_scope = lookupScope;
      }

      const displayMethod = this.hasDisplayMethodTarget ? this.displayMethodTarget.value.trim() : "";
      if (displayMethod && displayMethod !== baseDef.display_method) {
        config.display_method = displayMethod;
      }

      const searchExpression = this.hasSearchExpressionTarget ? this.searchExpressionTarget.value.trim() : "";
      if (searchExpression && searchExpression !== baseDef.search_expression) {
        config.search_expression = searchExpression;
      }

      const valueAttribute = this.hasValueAttributeTarget ? this.valueAttributeTarget.value.trim() : "";
      if (valueAttribute && valueAttribute !== (baseDef.value_attribute || "id")) {
        config.value_attribute = valueAttribute;
      }

      const isLinkable = this.hasLinkableTarget ? this.linkableTarget.checked : false;
      if (isLinkable !== (baseDef.linkable || false)) {
        config.linkable = isLinkable;
      }
      if (isLinkable) {
        const linkPathHelper = this.hasLinkPathHelperTarget ? this.linkPathHelperTarget.value.trim() : "";
        if (linkPathHelper && linkPathHelper !== baseDef.link_path_helper) {
          config.link_path_helper = linkPathHelper;
        }
      }

      const parentAttribute = this.hasParentAttributeTarget ? this.parentAttributeTarget.value.trim() : "";
      if (parentAttribute) {
        config.parent_attribute = parentAttribute;
      }

      const optgroupAttribute = this.hasOptgroupAttributeTarget ? this.optgroupAttributeTarget.value.trim() : "";
      if (optgroupAttribute) {
        config.optgroup_attribute = optgroupAttribute;
      }
    } else {
      // No lookup_key - include full custom configuration
      config.type = "foreign_key";

      const lookupModel = this.hasLookupModelTarget ? this.lookupModelTarget.value.trim() : "";
      if (lookupModel) {
        config.lookup_model = lookupModel;
      }

      const lookupScope = this.hasLookupScopeTarget ? this.lookupScopeTarget.value.trim() : "";
      if (lookupScope) {
        config.lookup_scope = lookupScope;
      }

      const displayMethod = this.hasDisplayMethodTarget ? this.displayMethodTarget.value.trim() : "";
      if (displayMethod) {
        config.display_method = displayMethod;
      }

      const searchExpression = this.hasSearchExpressionTarget ? this.searchExpressionTarget.value.trim() : "";
      if (searchExpression) {
        config.search_expression = searchExpression;
      }

      const valueAttribute = this.hasValueAttributeTarget ? this.valueAttributeTarget.value.trim() : "";
      if (valueAttribute) {
        config.value_attribute = valueAttribute;
      }

      const isLinkable = this.hasLinkableTarget ? this.linkableTarget.checked : false;
      if (isLinkable) {
        config.linkable = true;
        const linkPathHelper = this.hasLinkPathHelperTarget ? this.linkPathHelperTarget.value.trim() : "";
        if (linkPathHelper) {
          config.link_path_helper = linkPathHelper;
        }
      }

      const parentAttribute = this.hasParentAttributeTarget ? this.parentAttributeTarget.value.trim() : "";
      if (parentAttribute) {
        config.parent_attribute = parentAttribute;
      }

      const optgroupAttribute = this.hasOptgroupAttributeTarget ? this.optgroupAttributeTarget.value.trim() : "";
      if (optgroupAttribute) {
        config.optgroup_attribute = optgroupAttribute;
      }
    }

    return config;
  }
}
